/**
 * The one session the trainer clicked: what the popover offers, and what pressing Zapisz writes.
 *
 * ── The same override the drag writes, from a second path ────────────────────────────────────
 * A drag can only say "this day". A trainer who needs "this day, at 19:00 instead of 17:30" has no
 * gesture for it, and mobile solves it exactly here — `ScheduleStep.tsx` keeps `setWeekdayTime` for
 * the standing hour and `setOccurrenceOverride(coord, { date, time })` for one session, with a
 * restore that clears it. Both write `OccurrenceOverride`, so this module decides the popover's
 * fields and nothing about the wire.
 *
 * ── The time field exists only for a Stationary assignment ───────────────────────────────────
 * A Remote plan is scheduled date-only server-side and `assignRequest` drops a `time` from the body
 * for every mode but Stationary. Offering the field anyway would let a trainer set an hour that is
 * silently discarded, which is the defect the `timeOffPattern` refusal exists to prevent one layer
 * up.
 *
 * ── An edit that changes only the DATE keeps the hour the trainer gave this session ──────────
 * The two hours a session has — the standing weekday one and the one a trainer typed for it alone —
 * are both on the chip (`patternTime` and `time`), and only the first of them may be dropped from
 * the override. Comparing the typed hour against the chip's own was comparing it against itself.
 *
 * ── The picker cannot leave the session's own cycle ──────────────────────────────────────────
 * Owner decision, 22 September 2026, and the same rule the drag applies: a session stays in the
 * week its cycle occupies. The window is floored and capped to that week rather than only judged at
 * Zapisz, because a grid that draws a day the save refuses is a control that exists to say no —
 * which is exactly why the floor already sits at the start date.
 *
 * ── The time DEFAULTS to the weekday's, never to empty ───────────────────────────────────────
 * The popover opens on the hour the session already has — the standing weekday time, resolved the
 * generator's way by `projectionGrid` and carried on the chip. An empty field would read as "no
 * hour yet" on a session that has one, and saving it blank would be the trainer clearing a time
 * they never meant to touch.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import { atMidnight, parseIsoDate } from '~/shared/lib/calendarDate';

import { coordinateKey } from './assignSchedule';
import { cycleWindow } from './projectionDrop';
import { isTimeOfDay } from './stationaryTimes';
import type { DateWindow, OccurrenceOverride } from './assignRequest';
import type { ProjectionChip } from './projectionGrid';

/** What the popover draws. */
export interface OccurrenceEditFields {
  /** "YYYY-MM-DD", the date the picker opens on. */
  readonly date: string;
  /**
   * The days the picker may offer.
   *
   * Three bounds meet here, and the narrowest of each end wins: the assignable window opens TODAY,
   * the plan's start date is later than that whenever the trainer picked one, and the session's own
   * cycle week bounds both ends. Offering a day the save would refuse with `beforeStart` or
   * `outsideCycle` is a control that exists only to say no.
   */
  readonly window: DateWindow;
  /** "HH:mm", or `null` when this assignment books no hours and the field is absent. */
  readonly time: string | null;
  /** Whether this session is off its generated date, so „Przywróć" has something to undo. */
  readonly restorable: boolean;
}

/** Why the edit cannot be saved, or `null`. Same vocabulary a drop refuses in. */
export type OccurrenceEditRefusal =
  | 'malformedDate'
  | 'malformedTime'
  | 'outsideWindow'
  | 'beforeStart'
  | 'outsideCycle';

export type OccurrenceEditOutcome =
  | { readonly ok: false; readonly refusal: OccurrenceEditRefusal }
  | { readonly ok: true; readonly overrides: ReadonlyMap<string, OccurrenceOverride> };

export interface OccurrenceEditContext {
  readonly startDate: Date | null;
  readonly window: DateWindow;
  /** Only a Stationary assignment carries an hour — see the header. */
  readonly stationary: boolean;
}

/** What the popover opens on. */
export function occurrenceEditFields(
  chip: ProjectionChip,
  { startDate, window, stationary }: OccurrenceEditContext,
): OccurrenceEditFields {
  const cycle = cycleWindow(chip);
  const floors = [window.earliest, cycle.earliest];
  if (startDate !== null) floors.push(atMidnight(startDate));
  const earliest = floors.reduce((a, b) => (b.getTime() > a.getTime() ? b : a));
  const latest =
    cycle.latest.getTime() < window.latest.getTime() ? cycle.latest : window.latest;

  return {
    date: chip.iso,
    window: { earliest, latest },
    time: stationary ? chip.time : null,
    restorable: chip.iso !== chip.patternIso || chip.state === 'moved',
  };
}

/**
 * The override map after Zapisz — or the reason it cannot be applied.
 *
 * A saved edit that lands the session back on its generated date WITH its generated hour clears the
 * override rather than storing an identity one, for the same reason a drop back onto the pattern
 * day does. A date restored but an hour changed is still a real override, so the two are judged
 * together rather than by the date alone.
 */
export function applyOccurrenceEdit(
  overrides: ReadonlyMap<string, OccurrenceOverride>,
  chip: ProjectionChip,
  next: { readonly date: string; readonly time: string | null },
  context: OccurrenceEditContext,
): OccurrenceEditOutcome {
  const date = parseIsoDate(next.date);
  if (date === null) return { ok: false, refusal: 'malformedDate' };

  const day = date.getTime();
  if (day < context.window.earliest.getTime() || day > context.window.latest.getTime()) {
    return { ok: false, refusal: 'outsideWindow' };
  }
  if (context.startDate !== null && day < atMidnight(context.startDate).getTime()) {
    return { ok: false, refusal: 'beforeStart' };
  }
  const cycle = cycleWindow(chip);
  if (day < cycle.earliest.getTime() || day > cycle.latest.getTime()) {
    return { ok: false, refusal: 'outsideCycle' };
  }

  const time = context.stationary ? next.time : null;
  if (time !== null && time !== '' && !isTimeOfDay(time)) {
    return { ok: false, refusal: 'malformedTime' };
  }

  const map = new Map(overrides);
  const key = coordinateKey(chip.coordinate);
  // Judged against the PATTERN hour, never against the chip's current one. `chip.time` already
  // carries whatever override this session holds, so a session retimed to 19:15 compared equal to
  // itself, read as "the standing hour", and lost the trainer's hour the moment they changed only
  // the date. `applyDrop` carries the hour across a move for the same reason.
  const timeUnchanged = time === null || time === '' || time === chip.patternTime;

  if (next.date === chip.patternIso && timeUnchanged) {
    map.delete(key);
    return { ok: true, overrides: map };
  }

  map.set(key, { date, time: timeUnchanged ? null : time });
  return { ok: true, overrides: map };
}

/** „Przywróć": the session goes back to where the generator put it, at the weekday's own hour. */
export function restoreOccurrence(
  overrides: ReadonlyMap<string, OccurrenceOverride>,
  chip: ProjectionChip,
): ReadonlyMap<string, OccurrenceOverride> {
  const map = new Map(overrides);
  map.delete(coordinateKey(chip.coordinate));
  return map;
}
